import classNames from 'classnames';
import {
    FastField, Field, FieldArray, FieldArrayRenderProps, FieldProps, FormikProps, useFormikContext
} from 'formik';
import i18n from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    BladeProps, ColumnSettings, DataGridSelectionType, openBlade, SelectionBladeContainerProps,
    SelectionListBladeType
} from 'react-tools';

import {
    Button, Checkbox, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, Select,
    Typography
} from '@material-ui/core';

import {
    Autocomplete, FieldButton, GroupBox, MaxLengthTextField as TextField
} from '../../../../components';
import {
    CompanyOrigin, CountryInfo, EntityType, LicensingEntities, NCompany, SubscriptionPlans,
    TemplateLibrary
} from '../../../../dataStore';
import { countries } from '../../../../dataStore/countries';
import {
    getDataStoreCollection, getDataStoreCollectionStatus
} from '../../../../dataStore/selectors/genericSelectors';
import { helperText, validateEmptyField } from '../../../../utils/formik';
import { fetchTemplatesAsync } from '../../../duck/thunks';
import { CompanyInfoActions, CompanyInfoProps } from './company';
import { useStyles } from './company.jss';
import { originEnumToString, getAvailableSubs } from '../utils';

interface CompanyDetailsFieldsProps {
  formikBag: FormikProps<NCompany>;
}

type Props = CompanyInfoProps & CompanyInfoActions & BladeProps & CompanyDetailsFieldsProps;

export const CompanyDetailsFields: React.FunctionComponent<Props> = (props) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    values: {
      origin: originValue,
      externalCompanyId: externalCompanyIdValue,
      subscriptionPlan: subscriptionPlanValue,
    },
    setFieldValue,
  } = useFormikContext<NCompany>();

  const [templateSelectionBladeOpen, setTemplateSelectionBladeOpen] = useState(false);
  const workgroupTemplates = useSelector(getDataStoreCollection(EntityType.TemplateLibrary));

  const templateLibraryColumns: ColumnSettings[] = [
    {
      name: 'id',
      width: 120,
      filter: 'agTextColumnFilter',
    },
    {
      name: 'name',
      width: 320,
      lockVisibility: true,
      filter: 'agTextColumnFilter',
    },
  ];

  const openTemplateLibrarySelection = useCallback(() => {
    if (!templateSelectionBladeOpen) {
      dispatch(fetchTemplatesAsync());

      dispatch(
        openBlade<SelectionBladeContainerProps>(props.bladeId, SelectionListBladeType, {
          columns: templateLibraryColumns,
          identifier: 'templateLibraries',
          onClose: (items: TemplateLibrary[] | null) => {
            if (items !== null) {
              props.formikBag.setFieldValue('templateLibraries', items);
            }
            setTemplateSelectionBladeOpen(false);
          },
          selectionType: DataGridSelectionType.Multiple,
          title: t('templateLibraries.listTitle'),
          itemsSelector: getDataStoreCollection(EntityType.TemplateLibrary),
          selectedItems: props.formikBag.values.templateLibraries,
          isFetchingSelector: getDataStoreCollectionStatus(EntityType.TemplateLibrary),
          submitButtonLabel: t('templateLibraries.submitButtonLabel'),
        })
      );
      setTemplateSelectionBladeOpen(true);
    }
  }, [props.company.templateLibraries, workgroupTemplates]);

  // autocomplete company number for non-Harmony companies with 'originString-externalCompanyIdValue' format
  useEffect(() => {
    let originStr = originEnumToString(originValue);

    if (originValue != CompanyOrigin.Harmony) {
      setFieldValue('number', `${originStr}-${externalCompanyIdValue || ''}`);
    }
  }, [originValue, externalCompanyIdValue, setFieldValue]);

  // render subscription tiers based on origin value
  const renderSubsTiers = useCallback(() => {
    const subPlans = getAvailableSubs(originValue);

    if (!subPlans) {
      return [];
    }

    const result = subPlans.map((sub, index) => {
      return (
        <MenuItem key={`${sub.value}-${index.toString()}`} value={sub.value}>
          {sub.label}
        </MenuItem>
      );
    });

    return result;
  }, [props.isFetching, originValue, props.company.origin]);

  // reset company number to empty when changing origin from Magento to Harmony
  useEffect(() => {
    const subPlans = getAvailableSubs(originValue);

    if (!subPlans) {
      return;
    }

    if (!subPlans.find((sub) => sub.value === subscriptionPlanValue)) {
      setFieldValue('subscriptionPlan', subPlans[0].value);
    }
  }, [originValue]);

  useEffect(() => {
    if (originValue === CompanyOrigin.Harmony && props.company.id < 1) {
      setFieldValue('number', '');
    }
  }, [originValue]);

  return (
    <div className={classes.fieldsContainer}>
      <GroupBox title="Company">
        <div className={classes.horizontalContainer}>
          <div className={classNames([classes.vertical, classes.marginRight])}>
            <Field
              name="name"
              render={({ field, form }: FieldProps<NCompany>) => {
                return (
                  <TextField
                    variant="filled"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    autoFocus={true}
                    label={t<string>('newnity.company.name')}
                    helperText={helperText(form, field)}
                    error={validateEmptyField(form, field)}
                    {...field}
                    value={field.value || ''}
                    className={classes.formField}
                    inputProps={{ maxLength: 150 }}
                  />
                );
              }}
            />
            <FastField
              name="number"
              render={({ field, form }: FieldProps<NCompany>) => {
                return (
                  <TextField
                    variant="filled"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    label={t<string>('newnity.company.number')}
                    helperText={helperText(form, field)}
                    {...field}
                    error={validateEmptyField(form, field)}
                    value={field.value || ''}
                    className={classes.formField}
                    inputProps={{ maxLength: 255 }}
                  />
                );
              }}
            />
            <Field
              name="origin"
              render={({ field, form }: FieldProps<NCompany>) => (
                <FormControl disabled={props.company.id > 0} className={classes.formField}>
                  <InputLabel
                    style={{
                      marginLeft: '11px',
                      marginTop: '7px',
                    }}
                  >
                    {t<string>('newnity.company.origin')}
                  </InputLabel>
                  <Select {...field} variant="filled"                  
                    {...field}
                    onChange={(e: React.ChangeEvent<{
                      name?: string | undefined;
                      value: any;
                  }>, child: React.ReactNode) => {
                      const origin = e.target.value;
                      if (origin === CompanyOrigin.MagentoSmbUsa) {
                        setFieldValue('musicRecommendationEnabled', true);
                      } else {
                        setFieldValue('musicRecommendationEnabled', false);
                      }
                      field.onChange(e);
                    }}
                  >
                    <MenuItem value={CompanyOrigin.Harmony}>
                      {originEnumToString(CompanyOrigin.Harmony)}
                    </MenuItem>
                    <MenuItem value={CompanyOrigin.MagentoSmbUsa}>
                      {originEnumToString(CompanyOrigin.MagentoSmbUsa)}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Field
              name="subscriptionPlan"
              render={({ field, form }: FieldProps<NCompany>) => (
                <FormControl className={classes.formField}>
                  <InputLabel
                    style={{
                      marginLeft: '11px',
                      marginTop: '7px',
                    }}
                  >
                    {t<string>('newnity.company.subscriptionPlan')}
                  </InputLabel>
                  <Select {...field} variant="filled">
                    {renderSubsTiers()}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <div className={classNames([classes.vertical, classes.marginLeft])}>
            <Field
              name="parentId"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  label={t<string>('newnity.company.parentWorkgroup')}
                  {...field}
                  error={validateEmptyField(form, field)}
                  value={field.value || 1}
                  className={classes.formField}
                  helperText={helperText(form, field)}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <FieldButton
                          title={t<string>('create.workgroup.select_btn_label')}
                          onClick={() => {}}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <FastField
              name="licensingEntityId"
              render={({ field, form }: FieldProps<NCompany>) => (
                <FormControl className={classes.formField}>
                  <InputLabel
                    style={{
                      marginLeft: '11px',
                      marginTop: '7px',
                    }}
                  >
                    {t<string>('newnity.company.licensingEntityId')}
                  </InputLabel>
                  <Select {...field} variant="filled">
                    <MenuItem value={LicensingEntities.MoodMedia}>
                      {t('newnity.company.licensingEntity.MoodMedia')}
                    </MenuItem>
                    <MenuItem value={LicensingEntities.PlayNetwork}>
                      {t('newnity.company.licensingEntity.PlayNetwork')}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            {originValue !== CompanyOrigin.Harmony ? (
              <FastField
                name="externalCompanyId"
                render={({ field, form }: FieldProps<NCompany>) => {
                  return (
                    <TextField
                      variant="filled"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      label={t<string>('newnity.company.externalCompanyId')}
                      helperText={helperText(form, field)}
                      {...field}
                      onChange={e => {form.setFieldValue(field.name, e.target.value.trimStart());}}
                      error={validateEmptyField(form, field)}
                      value={field.value || ''}
                      className={classes.formField}
                      inputProps={{ maxLength: 50 }}
                    />
                  );
                }}
              />
            ) : (
              <></>
            )}
            {
              <Field
                name="musicRecommendationEnabled"
                render={({ form }: FieldProps<NCompany>) => (
                  <FormControl className={classes.formField} ><FormControlLabel style={{height:56}}
                    control={
                      <Checkbox
                        onClick={() => {
                          form.setFieldValue(
                            'musicRecommendationEnabled',
                            !form.values.musicRecommendationEnabled
                          );
                        }}
                        checked={form.values.musicRecommendationEnabled}
                        onChange={(_) => {}}
                        inputProps={{
                          name: 'musicRecommendationEnabled',
                          id: 'musicRecommendationEnabled',
                        }}
                      />
                    }
                    label={t('newnity.company.musicRecommendationEnabled')}
                  /></FormControl>
                )}
              />
            }
          </div>
        </div>
      </GroupBox>
      <GroupBox title="Template Libraries">
        <div className={classNames([classes.templateContainer, classes.marginRight])}>
          <FieldArray
            name="templateLibraries"
            render={(fieldProps: FieldArrayRenderProps) => {
              const templates = fieldProps.form.values.templateLibraries;
              return (
                <Typography variant="caption">
                  {templates && templates.length
                    ? `${templates.map((e: any) => e.name).join(', ')}`
                    : t('templateLibraries.empty')}
                </Typography>
              );
            }}
          />
          <Button variant="contained" color="secondary" onClick={openTemplateLibrarySelection}>
            {t('select')}
          </Button>
        </div>
      </GroupBox>
      <GroupBox title="Address">
        <div className={classes.horizontalContainer}>
          <div className={classNames([classes.vertical, classes.marginRight])}>
            <FastField
              name="country"
              render={({ field, form }: FieldProps<NCompany>) => (
                <Autocomplete
                  items={countries}
                  selectedItem={form.values.country}
                  minCharInput={2}
                  textFieldProps={{
                    name: 'country',
                    label: t('newnity.company.address.country'),
                  }}
                  onChange={(selectedItem: CountryInfo | undefined) => {
                    form.setFieldValue(field.name, selectedItem ? selectedItem.name : '');
                  }}
                />
              )}
            />
            <FastField
              name="city"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.address.city')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
            <FastField
              name="state"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.address.state')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
          </div>
          <div className={classNames([classes.vertical, classes.marginLeft])}>
            <FastField
              name="zipCode"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.address.zipCode')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 50 }}
                />
              )}
            />
            <FastField
              name="streetAddress"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  multiline
                  label={t<string>('newnity.company.address.streetAddress')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 150 }}
                />
              )}
            />
          </div>
        </div>
      </GroupBox>
      <GroupBox title="Contact">
        <div className={classes.horizontalContainer}>
          <div className={classNames([classes.vertical, classes.marginRight])}>
            <FastField
              name="firstName"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.contact.firstName')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 50 }}
                />
              )}
            />
            <FastField
              name="lastName"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.contact.lastName')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 50 }}
                />
              )}
            />
          </div>
          <div className={classNames([classes.vertical, classes.marginLeft])}>
            <FastField
              name="phone"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.contact.phone')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 50 }}
                />
              )}
            />
            <FastField
              name="email"
              render={({ field, form }: FieldProps<NCompany>) => (
                <TextField
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  label={t<string>('newnity.company.contact.email')}
                  {...field}
                  value={field.value || ''}
                  className={classes.formField}
                  inputProps={{ maxLength: 50 }}
                />
              )}
            />
          </div>
        </div>
      </GroupBox>
    </div>
  );
};
